/**
 * Tenant-portal session context (W79 PR B).
 *
 * Wraps the tenant-tier session that lives in `localStorage` (see
 * `auth/tenantSession.ts`) in a React context so every tenant page can
 * read `useTenantPortal()` without a per-page rehydrate.
 *
 * Exposes:
 *   - `session`        — the persisted `{ token, expiresAt, ... }` or null
 *   - `signOut()`      — clears storage + redirects to /tenant/sign-in
 *   - `setSession()`   — called by `<TenantSignInPage>` after a successful
 *                        POST /tenant/sign-in or accept-invitation flow
 *   - `getAccessToken()` — shape-compatible with `useAuth()` so the
 *                        existing `apiFetch` getter contract works
 *                        unchanged.
 */
import * as React from 'react';
import {
  clearTenantSession,
  readTenantSession,
  writeTenantSession,
  type TenantSession,
} from '@/auth/tenantSession';

interface TenantPortalContextValue {
  session: TenantSession | null;
  setSession: (session: TenantSession) => void;
  signOut: () => void;
  getAccessToken: () => string | null;
}

const TenantPortalContext = React.createContext<TenantPortalContextValue | null>(
  null,
);

export interface TenantPortalProviderProps {
  children: React.ReactNode;
}

export function TenantPortalProvider({
  children,
}: TenantPortalProviderProps): JSX.Element {
  const [session, setSessionState] = React.useState<TenantSession | null>(
    () => readTenantSession(),
  );

  React.useEffect(() => {
    function rehydrate(): void {
      setSessionState(readTenantSession());
    }
    window.addEventListener('senviro:tenant-session-changed', rehydrate);
    window.addEventListener('storage', rehydrate);
    return () => {
      window.removeEventListener('senviro:tenant-session-changed', rehydrate);
      window.removeEventListener('storage', rehydrate);
    };
  }, []);

  const setSession = React.useCallback((next: TenantSession) => {
    writeTenantSession(next);
    setSessionState(next);
  }, []);

  const signOut = React.useCallback(() => {
    clearTenantSession();
    setSessionState(null);
  }, []);

  const getAccessToken = React.useCallback(
    () => session?.token ?? null,
    [session],
  );

  const value = React.useMemo<TenantPortalContextValue>(
    () => ({ session, setSession, signOut, getAccessToken }),
    [session, setSession, signOut, getAccessToken],
  );

  return (
    <TenantPortalContext.Provider value={value}>
      {children}
    </TenantPortalContext.Provider>
  );
}

export function useTenantPortal(): TenantPortalContextValue {
  const ctx = React.useContext(TenantPortalContext);
  if (!ctx) {
    throw new Error(
      'useTenantPortal() must be used inside <TenantPortalProvider>',
    );
  }
  return ctx;
}
